<template>
  <div class="checkout__cart col-xl-6 col-lg-12 col-sm-12 col-xs-12">
    <div class="inner">
      <div class="title">
        <h3>In Your Cart</h3>
      </div>
      <div class="cart">
        <div v-loading="!isCheckoutLoaded || !isTaxesCalculated || isLoadingTaxes">
          <cart-products
            :items="items"
            @update-qty="() => fetchCheckout(fetchShipping)"
            @remove-line="() => fetchCheckout(fetchShipping)"
          ></cart-products>
          <div class="promotion_info" v-if="isHasPromotions">
            <div v-if="checkout.promotion_conditions_messages.length !== 0">
              <span v-for="message in checkout.promotion_conditions_messages" v-html="message"></span>
            </div>
            <div v-if="checkout.promotion_reward_message">
              <span v-html="checkout.promotion_reward_message"></span>
            </div>
          </div>
          <div class="cart__subtotal">
            <div class="item">
              <span>Subtotal</span>
              <span>${{ subTotal }}</span>
            </div>
            <div class="item">
              <span>Tax</span>
              <span>${{ taxTotal }}</span>
            </div>
            <div class="item" v-if="isShippingMethodSelectionVisible">
              <span>Shipping</span>
              <span v-if="checkout.coupon_free_shipping">Free</span>
              <span v-else>${{ shippingTotal }}</span>
            </div>
            <div class="item" v-if="checkout.discount_total > 0">
              <span>Discount</span>
              <span>{{ checkout.formatted_discount_total }}</span>
            </div>
            <div class="item" v-if="checkout.formatted_shipping_discount_total > 0">
              <span>Shipping Discount</span>
              <span>{{ checkout.formatted_shipping_discount_total }}</span>
            </div>
            <div class="item" v-if="checkout.promotion_discount != 0">
              <span v-if="checkout.promotion_title">Promotion Discount (@{{ checkout.promotion_title }})</span>
              <span v-if="!checkout.promotion_title">Promotion Discount</span>
              <span>-${{ checkout.promotion_discount }}</span>
            </div>
            <div class="item" v-if="isLoyaltyVisible">
              <span>{{ checkout.is_shipped_for_points ? 'Loyalty Shipping' : 'Loyalty' }}</span>
              <span>-${{ checkout.bonus_total }}</span>
            </div>
          </div>
        </div>
        <div data-pp-message data-pp-pageType="primary" :data-pp-amount="this.total"></div>
        <div v-if="isShippingMethodSelectionVisible" class="shipping_method">
          <shipping-methods></shipping-methods>
        </div>
        <cart-coupon></cart-coupon>
        <rewards></rewards>
        <div class="cart__promotion">
          <div class="form-group full-width">
            <div class="checkbox-group">
              <input v-model="isPromotionable"
                     id="promote-agreement"
                     type="checkbox">
              <label for="promote-agreement" tabindex="0">I want to receive emails about promotions and
                products <span
                  class="sr-only">{{ isPromotionable ? 'checked' : 'unchecked' }}</span></label>
              <span class="validation-errors" v-if="optOutPromotionsText && !isPromotionable">
                            {{ optOutPromotionsText }}
                        </span>
            </div>
          </div>
        </div>
        <div v-if="groupMessages.length !== 0" class="cart__group_messages">
          <ul>
            <li v-for="message in groupMessages"><i class="fas fa-info-circle"></i>@{{ message }}</li>
          </ul>
        </div>
        <div class="cart__total">
          <span>Total</span>
          <strong>${{ total }}</strong>
        </div>
      </div>
      <div style="text-align: center;">
        <div id="recaptcha-container" class="recaptcha-container"
             :class="{'alert alert-danger': errors.has('recaptcha')}"></div>
        <span class="validation-errors" v-for="error in errors.collect('recaptcha')">{{ error }}</span>
      </div>
      <button :disabled="isRequestProcessing || orderUnprocessable"
              v-if="isHidePlaceOrderButton === false"
              class="btn place-order"
              type="submit">
        Place Your Order
      </button>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import rewards from '@app/components/checkout/components/rewards.vue';
import shippingMethods from '@app/components/checkout/components/shippingMethods.vue';
import cartCoupon from '@app/components/checkout/components/coupon.vue';
import cartProducts from '@app/components/checkout/components/cartProducts.vue';

export default {
  name: 'products',
  components: {
    rewards,
    shippingMethods,
    cartCoupon,
    cartProducts,
  },
  inject: [
    '$validator',
  ],
  props: {
    optOutPromotionsText: String,
  },
  computed: {
    ...mapGetters('checkout', [
      'checkout',
      'orderUnprocessable',
      'items',
      'discountTotal',
      'total',
      'subTotal',
      'shippingTotal',
      'taxTotal',
      'isProcessable',
      'isRequestProcessing',
      'isLoadingShippingMethods',
      'isHasPromotions',
      'isHidePlaceOrderButton',
    ]),
    isShippingMethodSelectionVisible: function () {
      return this.items.filter(function (item) {
        const shippingType = item.options.shipping_type ? item.options.shipping_type : 'ship';

        return 'ship' === shippingType;
      }).length > 0;
    },
    isLoyaltyVisible: function () {
      return this.checkout.bonus_total && this.checkout.bonus_total != 0;
    },
    isDiscountVisible: function () {
      return this.checkout.discount_total > 0 &&
        this.checkout.coupons.filter(item => item.type !== 'free_shipping').length > 0;
    },
    isPromotionable: {
      get() {
        return this.$store.state.contact.is_promotionable;
      },
      set(value) {
        this.$store.commit('contact/setIsPromotional', value);
      },
    },
    contact() {
      return this.$store.state.contact;
    },
    groupMessages() {
      return this.$store.state.checkout.groupMessages;
    },
    isCheckoutLoaded() {
      return this.$store.state.checkout.isCheckoutLoaded;
    },
    isTaxesCalculated() {
      return this.$store.state.checkout.isTaxesCalculated
    },
    isLoadingTaxes() {
      return this.$store.state.checkout.isLoadingTaxes
    }
  },
  methods: {
    ...mapActions('checkout', [
      'fetchCheckout',
    ]),
    ...mapActions('shippingMethods', [
      'fetchShipping',
    ]),
  },
};
</script>

<style scoped lang="scss">
.recaptcha-container {
  display: inline-block;
  width: auto;
}
</style>
